import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { AdjustmentQuickButton } from '../components/AdjustmentQuickButton';
import { AdvanceApplicationsSection } from '../components/AdvanceApplicationsSection';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { UserSquare2, Plus, Trash2, Search, ChevronRight, FileText, FileSpreadsheet, HandCoins, Archive, ArchiveRestore, AlertTriangle, Upload, Check, Filter, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ImportResidentsDialog } from '../components/ImportResidentsDialog';
import { ResidentBillingServices } from '../components/ResidentBillingServices';
import { ResidentThirdPartyPayer } from '../components/ResidentThirdPartyPayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = [
  { v: 'activo', label: 'Activo', cls: 'bg-emerald-100 text-emerald-800' },
  { v: 'baja_temporal', label: 'Baja temporal', cls: 'bg-amber-100 text-amber-800' },
  { v: 'egreso_total', label: 'Egreso total', cls: 'bg-orange-100 text-orange-800' },
  { v: 'fallecido', label: 'Fallecido', cls: 'bg-slate-200 text-slate-800' },
];
const TERMINAL_STATUSES = ['egreso_total', 'fallecido'];

const IVA_CONDITIONS = [
  { v: 'consumidor_final', label: 'Consumidor Final' },
  { v: 'monotributo', label: 'Monotributista' },
  { v: 'responsable_inscripto', label: 'Responsable Inscripto' },
  { v: 'exento', label: 'Exento' },
];

const emptyResp = { responsible_id: '', name: '', dni: '', relationship: '', phone: '', email: '', is_payment_responsible: false };
const empty = {
  full_name: '', first_name: '', last_name: '',
  dni: '', cuil: '', birth_date: '', gender: '', address: '', phone: '', email: '',
  status: 'activo', bill_during_status: true,
  admission_date: '', discharge_date: '', room: '', floor: '',
  medical_insurance: '', insurance_member_number: '', primary_doctor: '', allergies: '', observations: '',
  responsibles: [], billing_target_type: 'resident', billing_target_responsible_id: null, billing_target_insurer_id: null,
  affiliate_type: '', affiliate_type_valid_from: '',
  acta_number: '', acta_date: '', acta_valid_from: '', acta_valid_until: '', acta_attachment_id: null,
  pami_incidence_from: '',
  fiscal_data: { legal_name: '', tax_id: '', iva_condition: 'consumidor_final', fiscal_address: '' },
};

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

function Residents() {
  const { user } = useOutletContext();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [osFilter, setOsFilter] = useState('');
  const [conceptFilter, setConceptFilter] = useState([]);
  const [conceptFilterOpen, setConceptFilterOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState({ open: false, mode: 'archive', resident: null, loading: false });
  const [importOpen, setImportOpen] = useState(false);
  const [dialog, setDialog] = useState({ open: false, data: empty });
  const [dialogTab, setDialogTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [abonoServices, setAbonoServices] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [cc, setCc] = useState(null);
  const [ccFrom, setCcFrom] = useState('');
  const [ccTo, setCcTo] = useState('');
  const [showAllCc, setShowAllCc] = useState(false); // v3.32.2 · false = sólo pendientes
  const ccMovementsView = useMemo(() => {
    const mv = cc?.movements || [];
    const filtered = mv.filter(m => {
      if (!showAllCc && m.has_pending === false) return false;
      const d = (m.date || '').slice(0, 10);
      if (!d) return true;
      if (ccFrom && d < ccFrom) return false;
      if (ccTo && d > ccTo) return false;
      return true;
    });
    // Más reciente arriba (orden cronológico descendente)
    return [...filtered].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [cc, ccFrom, ccTo, showAllCc]);
  const emptyAssign = { service_id: '', price_override: '', discount_pct: 0, quantity: 1, billing_target: '', billing_target_insurer_id: '', talonario: '', separate_invoice: false, valid_from: new Date().toISOString().slice(0, 10) };
  const [assignForm, setAssignForm] = useState(emptyAssign);
  const [cashBanks, setCashBanks] = useState([]);
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const emptyCollect = () => ({
    open: false, date: new Date().toISOString().slice(0, 10),
    lines: [{ payment_type_id: '', amount: '', reference: '', cash_bank_id: '' }],
    saving: false,
    // v3.28.13 · selección de comprobantes y pase a ARCA
    invoices: [],           // [{ ...pending_invoice, selected: bool, apply_amount: str }]
    passthrough_to_arca: false,
  });
  const [collect, setCollect] = useState(emptyCollect());
  const emptyAdv = () => ({ open: false, source_key: '', target_key: '', amount: '', saving: false });
  const [adv, setAdv] = useState(emptyAdv());
  const [advOpts, setAdvOpts] = useState({ sources: [], targets: [] });

  const canCreate = userCan(user, 'residentes', 'crear');
  const canEdit = userCan(user, 'residentes', 'editar');
  const canCollect = userCan(user, 'tesoreria_cobranzas', 'crear');

  const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const loadBilling = useCallback(async (rid) => {
    try {
      const [a, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/residents/${rid}/service-assignments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/residents/${rid}/account-statement`, { withCredentials: true }),
      ]);
      setAssignments(a.data || []); setCc(c.data || null);
    } catch (e) { /* noop */ }
  }, []);

  const addAssignment = async (forcedType) => {
    if (!assignForm.service_id) { toast.error('Elegí un servicio'); return; }
    const svc = abonoServices.find(s => s.service_id === assignForm.service_id);
    if (forcedType && svc && svc.billing_type !== forcedType) {
      toast.error(`Elegí un servicio de tipo "${forcedType}"`); return;
    }
    if (assignForm.billing_target === 'health_insurer' && !assignForm.billing_target_insurer_id) { toast.error('Elegí la obra social del pagador'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/residents/${dialog.data.resident_id}/service-assignments`, {
        resident_id: dialog.data.resident_id,
        service_id: assignForm.service_id,
        price_override: assignForm.price_override === '' ? null : Number(assignForm.price_override),
        discount_pct: Number(assignForm.discount_pct) || 0,
        quantity: Number(assignForm.quantity) || 1,
        billing_target: assignForm.billing_target || null,
        billing_target_insurer_id: assignForm.billing_target === 'health_insurer' ? assignForm.billing_target_insurer_id : null,
        talonario: assignForm.talonario || null,
        separate_invoice: !!assignForm.separate_invoice,
        valid_from: assignForm.valid_from,
      }, { withCredentials: true });
      toast.success('Servicio asignado'); setAssignForm(emptyAssign); loadBilling(dialog.data.resident_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const _svcType = (a) => {
    const svc = abonoServices.find(s => s.service_id === a.service_id);
    return svc?.billing_type || 'abono';
  };
  const _renderPagador = (a) => {
    if (!a.billing_target) return <span className="text-muted-foreground italic">Hereda</span>;
    if (a.billing_target === 'health_insurer') return `OS: ${insurers.find(o => o.insurer_id === a.billing_target_insurer_id)?.name || '—'}`;
    if (a.billing_target === 'responsible') return 'Familiar';
    return 'Residente';
  };
  const revokeAssignment = async (aid) => {
    try { await axios.delete(`${BACKEND_URL}/api/service-assignments/${aid}`, { withCredentials: true }); loadBilling(dialog.data.resident_id); }
    catch (e) { toast.error('Error'); }
  };

  const fetchAll = useCallback(async () => {
    try {
      const params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (showArchived) params.push('include_archived=true');
      const r = await axios.get(`${BACKEND_URL}/api/residents?${params.join('&')}`, { withCredentials: true });
      setList(r.data || []);
    } catch (e) { toast.error('Error'); }
  }, [search, statusFilter, showArchived]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // v3.47.9: si llegamos vía `?open=<resident_id>` (desde /residents/reports), abrir la
  // ficha directamente en modo EDICIÓN.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rid = params.get('open');
    if (!rid || list.length === 0) return;
    const r = list.find(x => x.resident_id === rid);
    if (!r) return;
    openEdit(r, params.get('tab') || 'personal');
    window.history.replaceState({}, '', '/residents');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  // v2.29.0: si llegamos vía `?open_collect=<resident_id>` (desde /billing/debtors), abrir cobro automático
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rid = params.get('open_collect');
    if (!rid || list.length === 0) return;
    const r = list.find(x => x.resident_id === rid);
    if (!r) return;
    openEdit(r);
    // Esperamos un tick a que se cargue cc/paymentTypes y abrimos el modal de cobro
    setTimeout(() => {
      if (paymentTypes.length > 0) openCollect();
    }, 600);
    // Limpiar el query param para que un refresh no lo dispare otra vez
    window.history.replaceState({}, '', '/residents');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/resident-services?include_inactive=false`, { withCredentials: true })
      .then(r => setAbonoServices(r.data || [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/health-insurers`, { withCredentials: true })
      .then(r => setInsurers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canCollect) return;
    Promise.all([
      axios.get(`${BACKEND_URL}/api/treasury/cash-banks/active`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/treasury/income-concepts/active`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/treasury/payment-types/active`, { withCredentials: true }),
    ]).then(([cb, ic, pt]) => {
      setCashBanks(cb.data || []); setIncomeConcepts(ic.data || []); setPaymentTypes(pt.data || []);
    }).catch(() => {});
  }, [canCollect]);

  const openCollect = async () => {
    const rid = dialog.data.resident_id;
    // v3.28.13 · cargar facturas pendientes con datos ARCA-elegibles
    let invoices = [];
    try {
      const r = await axios.get(`${BACKEND_URL}/api/residents/${rid}/pending-invoices`,
        { withCredentials: true });
      invoices = (r.data.invoices || []).map(inv => ({
        ...inv, selected: false, apply_amount: String(inv.outstanding),
      }));
      // v3.32.2 · Más reciente arriba (ordenar por fecha de emisión descendente)
      invoices.sort((a, b) => (b.issue_date || '').localeCompare(a.issue_date || ''));
    } catch { }
    const totalDefault = cc && cc.balance > 0 ? String(cc.balance) : '';
    // Pre-marcar FIFO hasta cubrir el saldo
    let remaining = Number(totalDefault) || 0;
    invoices = invoices.map(inv => {
      if (remaining <= 0) return { ...inv, selected: false };
      const pay = Math.min(remaining, inv.outstanding);
      remaining -= pay;
      return { ...inv, selected: true, apply_amount: String(pay) };
    });
    setCollect({
      ...emptyCollect(), open: true, invoices,
      lines: [{
        payment_type_id: paymentTypes[0]?.payment_type_id || '',
        amount: totalDefault, reference: '',
        cash_bank_id: cashBanks[0]?.cash_bank_id || '',
      }],
    });
  };

  const acceptsValueDate = (ptId) => {
    const n = (paymentTypes.find(p => p.payment_type_id === ptId)?.name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return n.includes('transferencia') || n.includes('debito');
  };
  const collectTotal = collect.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const setCollectLine = (i, k, v) => setCollect(c => { const lines = [...c.lines]; lines[i] = { ...lines[i], [k]: v }; return { ...c, lines }; });
  const addCollectLine = () => setCollect(c => ({ ...c, lines: [...c.lines, { payment_type_id: paymentTypes[0]?.payment_type_id || '', amount: '', reference: '', cash_bank_id: '' }] }));
  const delCollectLine = (i) => setCollect(c => ({ ...c, lines: c.lines.filter((_, idx) => idx !== i) }));

  // v3.28.13 · Selección de comprobantes
  const toggleCollectInvoice = (id) => setCollect(c => ({
    ...c,
    invoices: c.invoices.map(inv => inv.invoice_id === id
      ? { ...inv, selected: !inv.selected, apply_amount: !inv.selected ? String(inv.outstanding) : '0' }
      : inv),
  }));
  const setCollectInvoiceAmount = (id, val) => setCollect(c => ({
    ...c,
    invoices: c.invoices.map(inv => inv.invoice_id === id ? { ...inv, apply_amount: val } : inv),
  }));
  const collectAllocated = collect.invoices.filter(i => i.selected).reduce((s, i) => s + (Number(i.apply_amount) || 0), 0);
  const collectArcaEligible = collect.invoices.some(i => i.selected && i.arca_promotion_eligible);

  // v3.30.3 · Auto-passthrough por tipo de valor: bancarios → auto ARCA · efectivo/mixto → decide el usuario.
  const cashPaymentTypeIds = useMemo(() => new Set(
    paymentTypes.filter(pt => (pt.name || '').trim().toLowerCase() === 'efectivo').map(pt => pt.payment_type_id)
  ), [paymentTypes]);
  const hasCashValue = collect.lines.some(l => Number(l.amount) > 0 && cashPaymentTypeIds.has(l.payment_type_id));
  const hasBankValue = collect.lines.some(l => Number(l.amount) > 0 && l.payment_type_id && !cashPaymentTypeIds.has(l.payment_type_id));
  const allBankValues = hasBankValue && !hasCashValue;
  const allCashValues = hasCashValue && !hasBankValue;
  const isMixedValues = hasCashValue && hasBankValue;

  // Default del pase a ARCA según composición (se recomputa al cambiar líneas o selección de FCs).
  useEffect(() => {
    if (!collect.open) return;
    if (!collectArcaEligible) {
      if (collect.passthrough_to_arca) setCollect(c => ({ ...c, passthrough_to_arca: false }));
      return;
    }
    // ARCA-elegible: allBank→forzado true · mixed→default true · allCash→default false
    if (allBankValues && !collect.passthrough_to_arca) {
      setCollect(c => ({ ...c, passthrough_to_arca: true }));
    } else if (allCashValues && collect.passthrough_to_arca) {
      setCollect(c => ({ ...c, passthrough_to_arca: false }));
    } else if (isMixedValues && !collect.passthrough_to_arca) {
      setCollect(c => ({ ...c, passthrough_to_arca: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collect.open, collectArcaEligible, allBankValues, allCashValues, isMixedValues]);

  const submitCollect = async () => {
    if (collect.lines.some(l => !l.payment_type_id || !(Number(l.amount) > 0))) { toast.error('Revisá los valores (tipo y monto)'); return; }
    if (collect.lines.some(l => Number(l.amount) > 0 && !l.cash_bank_id)) { toast.error('Cada valor debe tener su Caja/Banco asignada'); return; }
    if (collectTotal <= 0) { toast.error('El total debe ser mayor a 0'); return; }
    const anySelected = collect.invoices.some(i => i.selected);
    setCollect(c => ({ ...c, saving: true }));
    try {
      const allocations = anySelected
        ? collect.invoices.filter(i => i.selected && Number(i.apply_amount) > 0)
            .map(i => ({ invoice_id: i.invoice_id, amount: Number(i.apply_amount) }))
        : null;
      const r = await axios.post(`${BACKEND_URL}/api/residents/${dialog.data.resident_id}/collect`, {
        date: collect.date,
        payment_details: collect.lines.map(l => ({ payment_type_id: l.payment_type_id, amount: Number(l.amount), reference: l.reference || null, cash_bank_id: l.cash_bank_id, value_date: (acceptsValueDate(l.payment_type_id) && l.value_date) || null })),
        allocations,
        auto_apply: !anySelected,
        passthrough_to_arca: collect.passthrough_to_arca && collectArcaEligible,
      }, { withCredentials: true });
      const a = r.data.advance;
      const promoted = r.data.promoted_to_arca || [];
      const promotedPartial = r.data.promoted_partial || [];
      const skipped = r.data.passthrough_skipped || [];
      let msg = `Cobranza ${r.data.collection_number} registrada`;
      if (a > 0) msg += ` · Anticipo $${a.toLocaleString('es-AR')}`;
      if (promoted.length) msg += ` · ${promoted.length} pasada(s) a ARCA`;
      if (promotedPartial.length) msg += ` · ${promotedPartial.length} pase(s) parcial(es) a ARCA`;
      toast.success(msg);
      promoted.forEach(p => toast.info(`${p.original_number} → ${p.arca_number}`));
      promotedPartial.forEach(p => toast.info(
        `Parcial: ${p.original_number} → ${p.arca_number} por $${Number(p.amount).toLocaleString('es-AR')} (NC ${p.nc_number}); resto pendiente $${Number(p.remaining_on_original).toLocaleString('es-AR')}`
      ));
      skipped.forEach(sk => toast.warning(`${sk.invoice_number}: ${sk.reason}`));
      if (r.data.collection_id) {
        window.open(`${BACKEND_URL}/api/treasury/collections/${r.data.collection_id}/pdf`, '_blank');
      }
      setCollect(emptyCollect());
      loadBilling(dialog.data.resident_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cobrar'); setCollect(c => ({ ...c, saving: false })); }
  };

  // v3.47.20 · origen (anticipos / AJCC a favor) → destino (FC/ND / AJCC que sube deuda)
  const srcKey = (s) => `${s.source_type}|${s.source_id || ''}`;
  const tgtKey = (t) => `${t.target_type}|${t.target_id}`;
  const advDefaultAmount = (opts, sk, tk) => {
    const s = opts.sources.find(x => srcKey(x) === sk);
    const t = opts.targets.find(x => tgtKey(x) === tk);
    return s && t ? String(Math.min(s.available || 0, t.outstanding || 0)) : '';
  };
  const openAdvance = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/residents/${dialog.data.resident_id}/apply-options`, { withCredentials: true });
      const opts = { sources: r.data?.sources || [], targets: r.data?.targets || [] };
      setAdvOpts(opts);
      const sk = opts.sources[0] ? srcKey(opts.sources[0]) : '';
      const tk = opts.targets[0] ? tgtKey(opts.targets[0]) : '';
      setAdv({ ...emptyAdv(), open: true, source_key: sk, target_key: tk, amount: advDefaultAmount(opts, sk, tk) });
    } catch (e) { toast.error('Error'); }
  };
  const onAdvChange = (field, val) => setAdv(a => {
    const next = { ...a, [field]: val };
    return { ...next, amount: advDefaultAmount(advOpts, next.source_key, next.target_key) };
  });
  const submitAdvance = async () => {
    if (!adv.source_key) { toast.error('Elegí el saldo a favor a usar'); return; }
    if (!adv.target_key) { toast.error('Elegí el comprobante a cancelar'); return; }
    const amt = Number(adv.amount);
    if (!amt || amt <= 0) { toast.error('Monto inválido'); return; }
    const [source_type, source_id] = adv.source_key.split('|');
    const [target_type, target_id] = adv.target_key.split('|');
    setAdv(a => ({ ...a, saving: true }));
    try {
      const r = await axios.post(`${BACKEND_URL}/api/residents/${dialog.data.resident_id}/apply-advance`,
        { source_type, source_id: source_id || null, target_type, target_id, amount: amt }, { withCredentials: true });
      toast.success(`Aplicado: $${r.data.applied.toLocaleString('es-AR')} a ${r.data.invoice_number}`);
      setAdv(emptyAdv());
      loadBilling(dialog.data.resident_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); setAdv(a => ({ ...a, saving: false })); }
  };

  const openNew = () => { setDialogTab('personal'); setAssignments([]); setCc(null); setDialog({ open: true, data: { ...empty, responsibles: [], fiscal_data: { ...empty.fiscal_data } } }); };
  // Para residentes legacy sin first_name / last_name: derivamos de full_name
  const splitName = (fullName) => {
    if (!fullName) return { first_name: '', last_name: '' };
    const s = String(fullName).trim();
    if (s.includes(',')) {
      const [last, first] = s.split(',').map(p => p.trim());
      return { last_name: last || '', first_name: first || '' };
    }
    const parts = s.split(/\s+/);
    if (parts.length === 1) return { last_name: parts[0], first_name: '' };
    return { last_name: parts[parts.length - 1], first_name: parts.slice(0, -1).join(' ') };
  };
  const openEdit = (r, tab = 'personal') => {
    setDialogTab(tab);
    setAssignments([]); setCc(null); loadBilling(r.resident_id);
    const seed = (r.first_name || r.last_name) ? r : { ...r, ...splitName(r.full_name) };
    const hist = r.affiliate_type_history || [];
    const vf = hist.length ? String(hist[hist.length - 1].valid_from || '').slice(0, 10) : '';
    setDialog({ open: true, data: { ...empty, ...seed, affiliate_type_valid_from: vf, fiscal_data: { ...empty.fiscal_data, ...(r.fiscal_data || {}) } } });
  };

  const setF = (path, val) => setDialog(prev => {
    const d = { ...prev.data };
    if (path.startsWith('fiscal_data.')) {
      d.fiscal_data = { ...d.fiscal_data, [path.replace('fiscal_data.', '')]: val };
    } else {
      d[path] = val;
    }
    return { ...prev, data: d };
  });

  /** Varios campos en una sola actualización (evita que un setF pise al anterior). */
  const setFields = (patch) => setDialog(prev => ({ ...prev, data: { ...prev.data, ...patch } }));

  const updResp = (i, k, v) => {
    const arr = [...dialog.data.responsibles];
    arr[i] = { ...arr[i], [k]: v };
    setDialog({ ...dialog, data: { ...dialog.data, responsibles: arr } });
  };
  const setPaymentResponsible = (i, checked) => {
    // Uno solo por residente: al tildar uno se destilda el anterior.
    const arr = dialog.data.responsibles.map((r, idx) => ({
      ...r, is_payment_responsible: checked ? idx === i : (idx === i ? false : r.is_payment_responsible),
    }));
    setDialog({ ...dialog, data: { ...dialog.data, responsibles: arr } });
  };
  const addResp = () => setDialog({ ...dialog, data: { ...dialog.data, responsibles: [...dialog.data.responsibles, { ...emptyResp, responsible_id: `tmp_${Date.now()}` }] } });
  const delResp = (i) => setDialog({ ...dialog, data: { ...dialog.data, responsibles: dialog.data.responsibles.filter((_, idx) => idx !== i) } });

  const save = async () => {
    const d = dialog.data;
    if ((!d.first_name || !d.last_name) && !d.full_name) { toast.error('Nombres y Apellidos son obligatorios'); return; }
    if (!d.dni) { toast.error('DNI requerido'); return; }
    if (!d.fiscal_data.legal_name || !d.fiscal_data.tax_id) { toast.error('Datos fiscales requeridos'); return; }
    if (d.billing_target_type === 'responsible' && !d.billing_target_responsible_id) { toast.error('Elegí el responsable de pago'); return; }
    if (d.billing_target_type === 'health_insurer' && !d.billing_target_insurer_id) { toast.error('Elegí la obra social'); return; }
    setSaving(true);
    try {
      if (d.resident_id) {
        await axios.put(`${BACKEND_URL}/api/residents/${d.resident_id}`, d, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/residents`, d, { withCredentials: true });
      }
      toast.success('Guardado'); setDialog({ open: false, data: empty }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const d = dialog.data;

  // ===== Archivar / Desarchivar =====
  const isTerminal = TERMINAL_STATUSES.includes(d?.status);
  const canArchiveNow = isTerminal && !!d?.discharge_date && !d?.is_archived;
  const requestArchive = () => setArchiveConfirm({ open: true, mode: 'archive', resident: d, loading: false });
  const requestUnarchive = () => setArchiveConfirm({ open: true, mode: 'unarchive', resident: d, loading: false });

  const confirmArchiveAction = async () => {
    const r = archiveConfirm.resident;
    if (!r?.resident_id) return;
    setArchiveConfirm(s => ({ ...s, loading: true }));
    try {
      const endpoint = archiveConfirm.mode === 'archive' ? 'archive' : 'unarchive';
      await axios.post(`${BACKEND_URL}/api/residents/${r.resident_id}/${endpoint}`, {}, { withCredentials: true });
      toast.success(archiveConfirm.mode === 'archive' ? 'Ficha archivada' : 'Ficha desarchivada');
      setArchiveConfirm({ open: false, mode: 'archive', resident: null, loading: false });
      // refrescar ficha abierta y el listado
      try {
        const fresh = await axios.get(`${BACKEND_URL}/api/residents/${r.resident_id}`, { withCredentials: true });
        setDialog(prev => prev.open ? { ...prev, data: { ...empty, ...fresh.data, fiscal_data: { ...empty.fiscal_data, ...(fresh.data.fiscal_data || {}) } } } : prev);
      } catch { /* noop */ }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
      setArchiveConfirm(s => ({ ...s, loading: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2"><UserSquare2 className="w-7 h-7 text-purple-500" /> Residentes</h1>
        <div className="flex gap-2">
          {canCreate && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-residents-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Upload className="w-4 h-4 mr-2" />Importar Excel</Button>}
          {canCreate && <Button onClick={openNew} data-testid="new-resident-btn"><Plus className="w-4 h-4 mr-2" />Nuevo residente</Button>}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[240px]"><Label className="text-xs">Buscar (nombre / DNI / CUIL)</Label>
              <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" /><Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="resident-search" /></div></div>
            <div><Label className="text-xs">Estado</Label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="flex h-10 w-48 rounded-md border px-2 text-sm" data-testid="res-status-filter">
                <option value="">Todos</option>{STATUS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select></div>
            <div><Label className="text-xs">Obra Social</Label>
              <select value={osFilter} onChange={(e) => setOsFilter(e.target.value)} className="flex h-10 w-48 rounded-md border px-2 text-sm" data-testid="res-os-filter">
                <option value="">Todas</option>
                <option value="__none__">— Sin OS —</option>
                {insurers.map(o => <option key={o.insurer_id} value={o.insurer_id}>{o.name}</option>)}
              </select></div>
            <div className="relative">
              <Label className="text-xs">Conceptos activos</Label>
              <button
                type="button"
                onClick={() => setConceptFilterOpen(v => !v)}
                className="flex h-10 min-w-[180px] items-center gap-2 rounded-md border px-3 text-sm bg-background hover:bg-muted/40"
                data-testid="res-concept-filter-btn"
              >
                <Filter className="w-3.5 h-3.5" />
                <span>{conceptFilter.length === 0 ? 'Todos' : `${conceptFilter.length} seleccionado(s)`}</span>
              </button>
              {conceptFilterOpen && (
                <div className="absolute z-30 top-full left-0 mt-1 w-72 max-h-72 overflow-auto rounded-md border bg-popover shadow-lg p-2 text-xs" data-testid="res-concept-filter-menu">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-muted-foreground">Filtrar por concepto</span>
                    {conceptFilter.length > 0 && (
                      <button className="text-[11px] text-blue-600 hover:underline" onClick={() => setConceptFilter([])}>Limpiar</button>
                    )}
                  </div>
                  {abonoServices.filter(s => s.billing_type === 'abono').map(s => {
                    const checked = conceptFilter.includes(s.service_id);
                    return (
                      <label key={s.service_id} className="flex items-center gap-2 py-1 px-1 hover:bg-muted/60 rounded cursor-pointer" data-testid={`res-concept-opt-${s.service_id}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setConceptFilter(prev => e.target.checked ? [...prev, s.service_id] : prev.filter(x => x !== s.service_id))}
                        />
                        <span className="truncate">{s.name}</span>
                      </label>
                    );
                  })}
                  <div className="mt-1 pt-1 border-t text-[10px] text-muted-foreground leading-tight">
                    Con varios seleccionados se muestran residentes que tengan <b>todos</b> los conceptos elegidos.
                  </div>
                </div>
              )}
            </div>
            <label className="text-xs flex items-center gap-2 h-10 px-3 rounded-md border bg-muted/40 cursor-pointer select-none" data-testid="show-archived-toggle-wrap">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="show-archived-toggle"
              />
              <span>Ver archivados</span>
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const conceptCols = abonoServices.filter(s => s.billing_type === 'abono');
            const filteredList = list.filter(r => {
              // Filtro OS
              if (osFilter === '__none__' && r.billing_target_insurer_id) return false;
              if (osFilter && osFilter !== '__none__' && r.billing_target_insurer_id !== osFilter) return false;
              // Filtro conceptos (AND lógico: debe tener todos los seleccionados)
              if (conceptFilter.length > 0) {
                const has = new Set(r.assigned_abono_service_ids || []);
                if (!conceptFilter.every(cid => has.has(cid))) return false;
              }
              return true;
            });
            const totalCols = 5 + conceptCols.length + 1; // Nombre, DNI, Hab, Estado, OS, ...conceptos, Abrir
            const shortLabel = (name) => {
              const t = (name || '').trim();
              if (!t) return '—';
              if (t.length <= 10) return t;
              // Si tiene múltiples palabras, abreviamos cada una (2-3 chars) y unimos con punto.
              const words = t.split(/\s+/).filter(Boolean);
              if (words.length >= 2) {
                return words.map((w, i) => i === 0 ? w.slice(0, 4) : w.slice(0, 4)).join('.').slice(0, 14);
              }
              return t.slice(0, 9) + '…';
            };
            return (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>DNI</TableHead>
              <TableHead>Habitación</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Obra Social</TableHead>
              {conceptCols.map(s => (
                <TableHead key={s.service_id} className="text-center whitespace-nowrap" title={s.name} data-testid={`res-col-${s.service_id}`}>
                  <span className="text-[11px]">{shortLabel(s.name)}</span>
                </TableHead>
              ))}
              <TableHead className="text-right">Abrir</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredList.length === 0 ? <TableRow><TableCell colSpan={totalCols} className="text-center text-muted-foreground">Sin residentes</TableCell></TableRow> :
                filteredList.map(r => {
                  const st = STATUS.find(s => s.v === r.status) || STATUS[0];
                  const os = insurers.find(o => o.insurer_id === r.billing_target_insurer_id);
                  const assignedSet = new Set(r.assigned_abono_service_ids || []);
                  return (
                    <TableRow
                      key={r.resident_id}
                      data-testid={`res-row-${r.resident_id}`}
                      onClick={() => canEdit && openEdit(r)}
                      className={`${canEdit ? 'cursor-pointer hover:bg-muted/60 transition-colors' : ''} ${r.is_archived ? 'opacity-60' : ''}`}
                      role={canEdit ? 'button' : undefined}
                      title={canEdit ? 'Abrir ficha del residente' : undefined}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{r.full_name}</span>
                          {r.is_archived && (
                            <Badge variant="outline" className="text-[10px] font-normal border-slate-400 text-slate-600 gap-1" data-testid={`archived-badge-${r.resident_id}`}>
                              <Archive className="w-3 h-3" />Archivada
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.dni}</TableCell>
                      <TableCell className="text-xs">{r.room ? `${r.floor || ''} ${r.room}` : '—'}</TableCell>
                      <TableCell><Badge className={st.cls}>{st.label}</Badge></TableCell>
                      <TableCell className="text-xs">
                        {os ? (
                          <div className="space-y-0.5">
                            <div className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />{os.name}</div>
                            {(os.affiliate_types || []).length > 0 && (
                              <div data-testid={`affiliate-cell-${r.resident_id}`}
                                   className={r.affiliate_type ? 'text-teal-700' : 'text-amber-700'}>
                                {r.affiliate_type || '⚠ sin tipo de afiliado'}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      {conceptCols.map(s => (
                        <TableCell key={s.service_id} className="text-center" data-testid={`res-cell-${r.resident_id}-${s.service_id}`}>
                          {assignedSet.has(s.service_id)
                            ? <Check className="w-4 h-4 inline text-emerald-600" aria-label="Asignado" />
                            : <span className="text-muted-foreground/40">—</span>}
                        </TableCell>
                      ))}
                      <TableCell className="text-right">{canEdit && <ChevronRight className="w-4 h-4 inline text-muted-foreground" />}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          </div>
            );
          })()}
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, data: empty })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span>{d.resident_id ? 'Editar' : 'Nuevo'} residente</span>
              {d.is_archived && (
                <Badge variant="outline" className="border-slate-400 text-slate-700 gap-1" data-testid="dialog-archived-badge">
                  <Archive className="w-3 h-3" />Ficha archivada
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {d.is_archived && (
            <div className="rounded-md border border-slate-300 bg-slate-50 dark:bg-slate-900/40 p-3 text-xs flex items-start gap-2" data-testid="archived-banner">
              <Archive className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-medium text-slate-700 dark:text-slate-200">Esta ficha está archivada</p>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  No se generarán nuevas facturas ni cobranzas. El historial se conserva intacto.
                  Si el residente reingresa, podés desarchivarla.
                </p>
              </div>
            </div>
          )}
          <Tabs key={`${d.resident_id || 'new'}-${dialogTab}`} defaultValue={dialogTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="personal">Personal</TabsTrigger>
              <TabsTrigger value="medico">Médico</TabsTrigger>
              <TabsTrigger value="resp">Responsables</TabsTrigger>
              <TabsTrigger value="facturacion" data-testid="tab-facturacion">Facturación</TabsTrigger>
              {d.resident_id && <TabsTrigger value="cc" data-testid="tab-cc">Cuenta Cte.</TabsTrigger>}
            </TabsList>

            <TabsContent value="personal" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombres *</Label><Input value={d.first_name || ''} onChange={(e) => setF('first_name', e.target.value)} data-testid="res-first-name" placeholder="Juan Carlos" /></div>
                <div><Label>Apellidos *</Label><Input value={d.last_name || ''} onChange={(e) => setF('last_name', e.target.value)} data-testid="res-last-name" placeholder="Pérez García" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>DNI *</Label><Input value={d.dni} onChange={(e) => setF('dni', e.target.value)} data-testid="res-dni" /></div>
                  <div><Label>CUIL</Label><Input value={d.cuil} onChange={(e) => setF('cuil', e.target.value)} /></div>
                </div>
                <div><Label>Fecha de nac.</Label><Input type="date" value={d.birth_date || ''} onChange={(e) => setF('birth_date', e.target.value)} /></div>
                <div><Label>Sexo</Label>
                  <select value={d.gender || ''} onChange={(e) => setF('gender', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm">
                    <option value="">—</option><option value="M">M</option><option value="F">F</option><option value="X">X</option>
                  </select></div>
                <div className="col-span-2"><Label>Dirección</Label><Input value={d.address || ''} onChange={(e) => setF('address', e.target.value)} /></div>
                <div><Label>Teléfono</Label><Input value={d.phone || ''} onChange={(e) => setF('phone', e.target.value)} /></div>
                <div><Label>Email</Label><Input value={d.email || ''} onChange={(e) => setF('email', e.target.value)} /></div>
              </div>
              <hr className="my-3" />
              <div className="grid grid-cols-4 gap-3">
                <div><Label>Estado</Label>
                  <select value={d.status} onChange={(e) => {
                    const next = e.target.value;
                    // v3.30.11.2 · Al pasar a Egreso total / Fallecido, autocompletamos la fecha
                    // de egreso con hoy si está vacía (para poder cortar la vigencia de los
                    // conceptos de facturación al guardar). Sigue siendo editable.
                    if (TERMINAL_STATUSES.includes(next) && !d.discharge_date) {
                      const today = new Date().toISOString().slice(0, 10);
                      setFields({ status: next, discharge_date: today });
                    } else {
                      setF('status', next);
                    }
                  }} className="flex h-10 w-full rounded-md border px-2 text-sm">
                    {STATUS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select></div>
                <div className="flex items-center pt-7"><span className="text-[11px] text-muted-foreground">{TERMINAL_STATUSES.includes(d.status) ? <><b>Facturación interrumpida</b> — al guardar, la vigencia de todos los conceptos se cortará al {d.discharge_date || 'día de hoy'}.</> : <>La opción «Facturar en este estado» se configura en la solapa <b>Facturación</b>.</>}</span></div>
                <div><Label>Habitación</Label><Input value={d.room || ''} onChange={(e) => setF('room', e.target.value)} /></div>
                <div><Label>Piso</Label><Input value={d.floor || ''} onChange={(e) => setF('floor', e.target.value)} /></div>
                <div><Label>Fecha ingreso</Label><Input type="date" value={d.admission_date || ''} onChange={(e) => setF('admission_date', e.target.value)} /></div>
                <div><Label>Fecha egreso</Label><Input type="date" value={d.discharge_date || ''} onChange={(e) => setF('discharge_date', e.target.value)} /></div>
              </div>
            </TabsContent>

            <TabsContent value="facturacion" className="space-y-4 pt-3" data-testid="tab-facturacion-content">
              {/* === Sección A: Datos fiscales === */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold">A · Datos fiscales del residente</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Razón social / Nombre *</Label><Input value={d.fiscal_data.legal_name} onChange={(e) => setF('fiscal_data.legal_name', e.target.value)} /></div>
                    <div><Label className="text-xs">CUIT / DNI *</Label><Input value={d.fiscal_data.tax_id} onChange={(e) => setF('fiscal_data.tax_id', e.target.value)} /></div>
                    <div><Label className="text-xs">Condición IVA</Label>
                      <select value={d.fiscal_data.iva_condition} onChange={(e) => setF('fiscal_data.iva_condition', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm">
                        {IVA_CONDITIONS.map(i => <option key={i.v} value={i.v}>{i.label}</option>)}
                      </select></div>
                    <div><Label className="text-xs">Domicilio fiscal</Label><Input value={d.fiscal_data.fiscal_address || ''} onChange={(e) => setF('fiscal_data.fiscal_address', e.target.value)} /></div>
                  </div>
                </CardContent>
              </Card>

              {/* === Resumen del convenio (se configura en la solapa Médico) === */}
              <Card data-testid="convenio-summary">
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <HandCoins className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-semibold">B · Convenio y excepciones</h3>
                  </div>
                  {(() => {
                    const os = insurers.find(o => o.insurer_id === d.billing_target_insurer_id);
                    const payer = (d.responsibles || []).find(r => r.is_payment_responsible);
                    return (
                      <>
                        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs space-y-1">
                          {os ? (
                            <div><b>Convenio:</b> {os.name} · se factura {os.receptor === 'resident' ? 'al residente / familiar' : 'a la obra social'} · talonario {(os.talonario || 'proforma').toUpperCase()}{os.receptor !== 'resident' ? ` · ${os.billing_modality === 'consolidada' ? 'agrupada' : 'una factura por residente'}` : ''}</div>
                          ) : (
                            <div className="text-amber-700"><b>Sin convenio.</b> Elegilo en la solapa <b>Médico</b>: sin convenio no se pueden facturar los conceptos prestacionales.</div>
                          )}
                          <div>
                            <b>Responsable de pago:</b>{' '}
                            {payer
                              ? <>{payer.name}{payer.relationship ? ` (${payer.relationship})` : ''}{payer.tax_id ? ` · CUIT ${payer.tax_id}` : <span className="text-amber-700"> · sin CUIT: no puede recibir comprobantes ARCA</span>}</>
                              : <span className="text-muted-foreground">ninguno — todo se factura al residente</span>}
                          </div>
                          <div className="text-muted-foreground">El convenio se configura en la solapa <b>Médico</b> y el responsable de pago en la solapa <b>Responsables</b>.</div>
                        </div>
                        <label className="flex items-center gap-2 text-xs cursor-pointer pt-1" data-testid="bill-during-status-wrap">
                          <input type="checkbox" checked={!!d.bill_during_status}
                                 onChange={(e) => setF('bill_during_status', e.target.checked)}
                                 data-testid="bill-during-status" />
                          <span><b>Facturar en este estado</b> — si está apagado, el residente queda fuera de la corrida masiva (estado actual: {STATUS.find(s => s.v === d.status)?.label})</span>
                        </label>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {d.resident_id && (
                <>
              <ResidentBillingServices
                residentId={d.resident_id}
                resident={d}
                services={abonoServices}
                insurers={insurers}
                assignments={assignments}
                canEdit={canEdit}
                onChange={() => loadBilling(d.resident_id)}
              />

              <ResidentThirdPartyPayer
                residentId={d.resident_id}
                resident={d}
                services={abonoServices}
                canEdit={canEdit}
                onChange={() => loadBilling(d.resident_id)}
              />

                  {/* === Sección D: Servicios puntuales habituales === */}
                  <Card>
                    <CardContent className="pt-4 space-y-3" data-testid="puntuales-section">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">📋</span>
                        <h3 className="text-sm font-semibold">D · Servicios puntuales habituales (NO entran en la masiva)</h3>
                      </div>
                      <p className="text-xs text-muted-foreground">Servicios eventuales (medicación adicional, asistencia especial, etc.). Aparecen como sugerencia rápida cuando hacés una factura manual y elegís a este residente.</p>
                      <Card className="bg-muted/30"><CardContent className="pt-4 pb-3 space-y-2">
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-5"><Label className="text-xs">Servicio puntual</Label>
                            <select value={assignForm.service_id && (abonoServices.find(s => s.service_id === assignForm.service_id)?.billing_type === 'puntual') ? assignForm.service_id : ''} onChange={(e) => setAssignForm({ ...assignForm, service_id: e.target.value, billing_type_filter: 'puntual' })} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="assign-service-puntual">
                              <option value="">Seleccionar...</option>
                              {abonoServices.filter(s => s.billing_type === 'puntual').map(s => <option key={s.service_id} value={s.service_id}>{s.name} — {fmtMoney(s.current_price)} · {s.talonario.toUpperCase()}</option>)}
                            </select></div>
                          <div className="col-span-3"><Label className="text-xs">Precio sugerido (opc.)</Label><MoneyInput value={assignForm.price_override} onChange={(e) => setAssignForm({ ...assignForm, price_override: e.target.value })} placeholder="Lista" /></div>
                          <div className="col-span-3"><Label className="text-xs">Talonario</Label>
                            <select value={assignForm.talonario || ''} onChange={(e) => setAssignForm({ ...assignForm, talonario: e.target.value })} className="flex h-10 w-full rounded-md border px-2 text-sm">
                              <option value="">Hereda del servicio</option><option value="proforma">Proforma</option><option value="arca">ARCA</option>
                            </select></div>
                          <div className="col-span-1"><Button className="w-full" size="sm" onClick={() => addAssignment('puntual')} disabled={!canEdit} data-testid="add-puntual-btn"><Plus className="w-4 h-4" /></Button></div>
                        </div>
                      </CardContent></Card>
                      <Table>
                        <TableHeader><TableRow><TableHead>Servicio</TableHead><TableHead>Precio sugerido</TableHead><TableHead>Talon.</TableHead><TableHead>Pagador override</TableHead><TableHead className="text-right">Acción</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {assignments.filter(a => a.is_active && _svcType(a) === 'puntual').length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-xs py-4">Sin puntuales sugeridos</TableCell></TableRow> :
                            assignments.filter(a => a.is_active && _svcType(a) === 'puntual').map(a => (
                              <TableRow key={a.assignment_id} data-testid={`puntual-row-${a.assignment_id}`}>
                                <TableCell className="font-medium text-xs">{a.service_name}</TableCell>
                                <TableCell className="text-xs">{a.price_override != null ? fmtMoney(a.price_override) : 'Lista'}</TableCell>
                                <TableCell className="text-xs">
                                  {a.talonario
                                    ? <Badge variant="outline" className={`text-[10px] ${a.talonario === 'arca' ? 'border-violet-300 text-violet-700' : 'border-blue-300 text-blue-700'}`}>{a.talonario.toUpperCase()}</Badge>
                                    : <span className="text-muted-foreground italic">Hereda</span>}
                                </TableCell>
                                <TableCell className="text-xs">{_renderPagador(a)}</TableCell>
                                <TableCell className="text-right">{canEdit && <Button variant="ghost" size="icon" className="text-red-600 h-7 w-7" onClick={() => revokeAssignment(a.assignment_id)}><Trash2 className="w-4 h-4" /></Button>}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            <TabsContent value="resp" className="space-y-3 pt-3">
              <div className="flex justify-between items-center"><span className="text-sm">Familiares / responsables</span><Button size="sm" variant="outline" onClick={addResp}><Plus className="w-4 h-4 mr-1" />Agregar</Button></div>
              {d.responsibles.length === 0 ? <p className="text-sm text-muted-foreground">Ningún responsable cargado.</p> :
                d.responsibles.map((r, i) => (
                  <Card key={i}><CardContent className="pt-4 grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3"><Label className="text-xs">Nombre</Label><Input value={r.name} onChange={(e) => updResp(i, 'name', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">DNI</Label><Input value={r.dni} onChange={(e) => updResp(i, 'dni', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Vínculo</Label><Input value={r.relationship || ''} onChange={(e) => updResp(i, 'relationship', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Tel</Label><Input value={r.phone || ''} onChange={(e) => updResp(i, 'phone', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Email</Label><Input value={r.email || ''} onChange={(e) => updResp(i, 'email', e.target.value)} /></div>
                    <div className="col-span-1 flex items-center gap-1"><Button variant="ghost" size="icon" onClick={() => delResp(i)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button></div>
                    <div className="col-span-12"><label className="text-xs flex gap-2 cursor-pointer"><input type="checkbox" checked={!!r.is_payment_responsible} onChange={(e) => setPaymentResponsible(i, e.target.checked)} data-testid={`resp-payer-${i}`} /> <span>Es responsable de pago <span className="text-muted-foreground">(uno solo por residente — recibe los comprobantes que se le facturan)</span></span></label></div>
                    {r.is_payment_responsible && (
                      <div className="col-span-12 rounded-md border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-950/20 p-3 grid grid-cols-12 gap-2 items-end" data-testid={`resp-fiscal-${i}`}>
                        <div className="col-span-12 text-[11px] text-indigo-900 dark:text-indigo-200">Datos fiscales para emitirle comprobantes. La <b>condición de IVA</b> define la letra (A si es Responsable Inscripto, B en el resto).</div>
                        <div className="col-span-3"><Label className="text-xs">CUIT / CUIL</Label><Input value={r.tax_id || ''} onChange={(e) => updResp(i, 'tax_id', e.target.value)} placeholder="20-24270270-3" data-testid={`resp-taxid-${i}`} /></div>
                        <div className="col-span-4"><Label className="text-xs">Condición de IVA</Label>
                          <select value={r.iva_condition || 'consumidor_final'} onChange={(e) => updResp(i, 'iva_condition', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid={`resp-iva-${i}`}>
                            <option value="consumidor_final">Consumidor Final</option>
                            <option value="monotributo">Monotributo</option>
                            <option value="responsable_inscripto">Responsable Inscripto</option>
                            <option value="exento">Exento</option>
                          </select></div>
                        <div className="col-span-5"><Label className="text-xs">Domicilio fiscal</Label><Input value={r.fiscal_address || ''} onChange={(e) => updResp(i, 'fiscal_address', e.target.value)} placeholder="Si queda vacío se usa el del residente" data-testid={`resp-address-${i}`} /></div>
                        {!r.tax_id && <div className="col-span-12 text-[11px] text-amber-700 dark:text-amber-300">⚠️ Sin CUIT/CUIL no se le puede emitir un comprobante ARCA a su nombre.</div>}
                      </div>
                    )}
                  </CardContent></Card>
                ))}
            </TabsContent>

            <TabsContent value="medico" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Convenio *</Label>
                  <select value={d.billing_target_insurer_id || ''}
                          onChange={(e) => {
                            const v = e.target.value || null;
                            const os = insurers.find(o => o.insurer_id === v);
                            setFields({
                              billing_target_insurer_id: v,
                              ...(os ? { medical_insurance: os.name } : {}),
                              ...((!v && d.billing_target_type === 'health_insurer') ? { billing_target_type: 'resident' } : {}),
                            });
                          }}
                          className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="billing-target-insurer">
                    <option value="">— Sin convenio —</option>
                    {insurers.map(os => <option key={os.insurer_id} value={os.insurer_id}>{os.name}</option>)}
                  </select>
                  {!d.billing_target_insurer_id ? (
                    <p className="text-[11px] text-amber-700 mt-1">
                      Sin convenio no se pueden facturar los conceptos prestacionales.
                      {d.medical_insurance ? ` En la ficha figuraba «${d.medical_insurance}».` : ''}
                    </p>
                  ) : (() => {
                    const os = insurers.find(o => o.insurer_id === d.billing_target_insurer_id);
                    return os ? (
                      <p className="text-[11px] text-muted-foreground mt-1" data-testid="convenio-info">
                        Se factura <b>{os.receptor === 'resident' ? 'al residente / familiar' : 'a la obra social'}</b> ·
                        talonario <b>{(os.talonario || 'proforma').toUpperCase()}</b>
                        {os.receptor !== 'resident' ? ` · ${os.billing_modality === 'consolidada' ? 'agrupada con el resto del convenio' : 'una factura por residente'}` : ''}
                      </p>
                    ) : null;
                  })()}
                </div>
                <div><Label>N° de Afiliado</Label><Input value={d.insurance_member_number || ''} onChange={(e) => setF('insurance_member_number', e.target.value)} placeholder="150075315204/00" data-testid="res-insurance-member" /></div>
                {/* v3.28.18 · Incidencia PAMI (visible sólo cuando el convenio es PAMI) */}
                {(() => {
                  const os = insurers.find(o => o.insurer_id === d.billing_target_insurer_id);
                  const isPami = (os?.name || '').toUpperCase() === 'PAMI';
                  if (!isPami) return null;
                  const today = new Date().toISOString().slice(0, 10);
                  const vf = d.pami_incidence_from || '';
                  let alertClass = 'text-muted-foreground'; let alertMsg = '';
                  if (!vf) {
                    alertClass = 'text-amber-700';
                    alertMsg = 'Sin Incidencia cargada: las prestaciones se facturan al PAMI pero impactan la CC del residente.';
                  } else if (vf > today) {
                    const dtn = new Date(today); const dtf = new Date(vf);
                    const days = Math.ceil((dtf - dtn) / 86400000);
                    alertClass = 'text-amber-700';
                    alertMsg = `Incidencia futura: arranca en ${days} días.`;
                  } else {
                    alertClass = 'text-emerald-700';
                    alertMsg = `Incidencia vigente desde ${vf.split('-').reverse().join('/')}. No caduca hasta la baja del residente.`;
                  }
                  return (
                    <div className="col-span-2 grid grid-cols-2 gap-3">
                      <div><Label>Incidencia PAMI · Desde</Label>
                        <Input type="date" value={vf}
                               onChange={(e) => setF('pami_incidence_from', e.target.value)}
                               data-testid="res-pami-incidence-from" />
                        <p className={`text-[11px] mt-1 ${alertClass}`}>{alertMsg}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Días entre Ingreso e Incidencia</Label>
                        <div className="h-10 flex items-center px-2 text-sm text-muted-foreground border rounded-md bg-slate-50">
                          {vf && d.admission_date
                            ? `${Math.round((new Date(vf) - new Date(d.admission_date)) / 86400000)} días`
                            : '—'}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  const os = insurers.find(o => o.insurer_id === d.billing_target_insurer_id);
                  const types = os?.affiliate_types || [];
                  if (types.length === 0) return null;
                  const hist = d.affiliate_type_history || [];
                  const last = hist.length ? hist[hist.length - 1] : null;
                  const isIoma = (os?.name || '').toUpperCase() === 'IOMA';
                  return (
                    <>
                      <div><Label>Tipo de afiliado * ({os.name})</Label>
                        <select value={d.affiliate_type || ''}
                                onChange={(e) => setFields({
                                  affiliate_type: e.target.value,
                                  affiliate_type_valid_from: d.affiliate_type_valid_from || new Date().toISOString().slice(0, 10),
                                })}
                                className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="res-affiliate-type">
                          <option value="">— Elegir tipo —</option>
                          {types.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        {!d.affiliate_type
                          ? <p className="text-[11px] text-amber-700 mt-1">{os.name} factura agrupado por tipo de afiliado: sin este dato el residente queda afuera de la corrida masiva.</p>
                          : last && <p className="text-[11px] text-muted-foreground mt-1" data-testid="res-affiliate-hist">Tipo cargado por {last.set_by_name || '—'}{last.acta_number ? ` · Acta N.º ${last.acta_number}` : ''}</p>}
                      </div>

                      {/* v3.28.14/15 · Datos del ACTA — SOLO IOMA */}
                      {isIoma && (() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const vf = d.acta_valid_from || '';
                        const vu = d.acta_valid_until || '';
                        let alertClass = 'text-muted-foreground';
                        let alertMsg = '';
                        if (!d.acta_number || !vf || !vu) {
                          alertClass = 'text-amber-700';
                          alertMsg = 'ACTA incompleta: sin datos, 300/310 se emiten al IOMA pero impactan la CC del residente.';
                        } else if (vu < today) {
                          alertClass = 'text-red-700 font-medium';
                          alertMsg = `ACTA VENCIDA el ${vu.split('-').reverse().join('/')}. Renová para no perder cobertura.`;
                        } else {
                          const dtu = new Date(vu);
                          const dtn = new Date(today);
                          const days = Math.ceil((dtu - dtn) / 86400000);
                          if (days <= 30) { alertClass = 'text-red-700'; alertMsg = `ACTA vence en ${days} días.`; }
                          else if (days <= 60) { alertClass = 'text-amber-700'; alertMsg = `ACTA vence en ${days} días.`; }
                          else { alertClass = 'text-emerald-700'; alertMsg = `ACTA vigente hasta ${vu.split('-').reverse().join('/')}.`; }
                        }
                        return (
                          <>
                            <div><Label>N.º de Acta</Label>
                              <Input value={d.acta_number || ''} onChange={(e) => setF('acta_number', e.target.value)}
                                     placeholder="Ej: 12345/2026" data-testid="res-acta-number" />
                              {!d.acta_number && d.affiliate_type && (
                                <p className="text-[11px] text-amber-700 mt-1">Cargá el N.º de Acta que autoriza al residente en {os.name}. Sin este dato queda incompleto para auditoría.</p>
                              )}
                            </div>
                            <div><Label>Fecha del Acta</Label>
                              <Input type="date" value={d.acta_date || ''} onChange={(e) => setF('acta_date', e.target.value)}
                                     data-testid="res-acta-date" />
                              <p className="text-[11px] text-muted-foreground mt-1">Fecha del documento emitido por {os.name}.</p>
                            </div>
                            <div><Label>Vigencia del Acta · Desde</Label>
                              <Input type="date" value={d.acta_valid_from || ''}
                                     onChange={(e) => {
                                       const v = e.target.value;
                                       setF('acta_valid_from', v);
                                       // Auto-sugerir "hasta" = mismo día del año siguiente (editable)
                                       // Sólo si el año es razonable, para no disparar mientras se tipea
                                       if (v && !d.acta_valid_until) {
                                         const [y, m, day] = v.split('-').map(n => parseInt(n, 10));
                                         if (y >= 1900 && m >= 1 && m <= 12 && day >= 1 && day <= 31) {
                                           const yy = String(y + 1);
                                           const mm = String(m).padStart(2, '0');
                                           const dd = String(day).padStart(2, '0');
                                           setF('acta_valid_until', `${yy}-${mm}-${dd}`);
                                         }
                                       }
                                     }}
                                     data-testid="res-acta-valid-from" />
                              <p className="text-[11px] text-muted-foreground mt-1">Desde esta fecha se puede facturar 300/310 a {os.name}. Al cargarla, se sugiere 1 año de vigencia (editable).</p>
                            </div>
                            <div><Label>Vigencia del Acta · Hasta</Label>
                              <Input type="date" value={d.acta_valid_until || ''} onChange={(e) => setF('acta_valid_until', e.target.value)}
                                     data-testid="res-acta-valid-until" />
                              <p className={`text-[11px] mt-1 ${alertClass}`}>{alertMsg || 'Hasta esta fecha rige el ACTA.'}</p>
                            </div>
                            <div className="col-span-2"><Label>Archivo del Acta (PDF / imagen)</Label>
                              <div className="flex items-center gap-2">
                                <Input type="file" accept="application/pdf,image/*"
                                       onChange={async (e) => {
                                         const file = e.target.files?.[0];
                                         if (!file) return;
                                         if (!d.resident_id) { toast.error('Guardá primero el residente y volvé a editarlo para poder subir archivos.'); return; }
                                         const fd = new FormData(); fd.append('file', file);
                                         try {
                                           const r = await axios.post(`${BACKEND_URL}/api/attachments/resident/${d.resident_id}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                                           setF('acta_attachment_id', r.data.attachment_id);
                                           toast.success('Archivo del Acta subido');
                                         } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo subir el archivo'); }
                                         e.target.value = '';
                                       }}
                                       data-testid="res-acta-file" />
                                {d.acta_attachment_id && (
                                  <>
                                    <Button type="button" variant="outline" size="sm" asChild data-testid="res-acta-download">
                                      <a href={`${BACKEND_URL}/api/attachments/file/${d.acta_attachment_id}`} target="_blank" rel="noreferrer">Ver</a>
                                    </Button>
                                    <Button type="button" variant="ghost" size="sm" onClick={() => setF('acta_attachment_id', null)} data-testid="res-acta-clear">
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  );
                })()}
                <div><Label>Médico cabecera</Label><Input value={d.primary_doctor || ''} onChange={(e) => setF('primary_doctor', e.target.value)} /></div>
              </div>
              <div><Label>Alergias</Label><Textarea rows={2} value={d.allergies || ''} onChange={(e) => setF('allergies', e.target.value)} /></div>
              <div><Label>Observaciones</Label><Textarea rows={3} value={d.observations || ''} onChange={(e) => setF('observations', e.target.value)} /></div>
            </TabsContent>

            {d.resident_id && (
              <TabsContent value="cc" className="space-y-3 pt-3" data-testid="cc-content">
                <div className="flex justify-end gap-2">
                  {canCollect && cc && (
                    <Button size="sm" onClick={openCollect} data-testid="collect-btn"><HandCoins className="w-4 h-4 mr-1" />Cobrar</Button>
                  )}
                  <AdjustmentQuickButton
                    targetType="resident"
                    targetId={d.resident_id}
                    targetLabel={`${d.last_name || ''}, ${d.first_name || ''}`}
                    onDone={() => loadBilling(d.resident_id)}
                  />
                  <Button size="sm" variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/residents/${d.resident_id}/account-statement/pdf${(ccFrom || ccTo) ? `?${[ccFrom && `from_date=${ccFrom}`, ccTo && `to_date=${ccTo}`].filter(Boolean).join('&')}` : ''}`, '_blank')} data-testid="cc-pdf-btn"><FileText className="w-4 h-4 mr-1" />Estado de cuenta (PDF)</Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/residents/${d.resident_id}/account-statement/excel${(ccFrom || ccTo) ? `?${[ccFrom && `from_date=${ccFrom}`, ccTo && `to_date=${ccTo}`].filter(Boolean).join('&')}` : ''}`, '_blank')} data-testid="cc-excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
                </div>
                {cc && (
                  <div className="grid grid-cols-3 gap-3">
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Total DEBE</div><div className="text-lg font-bold">{fmtMoney(cc.total_debit)}</div></CardContent></Card>
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Total HABER</div><div className="text-lg font-bold">{fmtMoney(cc.total_credit)}</div></CardContent></Card>
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Saldo</div><div className={`text-lg font-bold ${cc.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="cc-balance">{fmtMoney(cc.balance)}</div><div className="text-[10px] text-muted-foreground">{cc.balance > 0 ? 'El residente debe' : cc.balance < 0 ? 'Saldo a favor' : 'Sin saldo'}</div></CardContent></Card>
                  </div>
                )}
                {(cc?.available_advance > 0 || cc?.available_cc_adjustment_credit > 0) && (
                  <div className="flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="cc-advance">
                    <div className="text-sm space-x-3">
                      {cc.available_advance > 0 && <span>Anticipo disponible: <b className="text-emerald-700">{fmtMoney(cc.available_advance)}</b></span>}
                      {cc.available_cc_adjustment_credit > 0 && <span data-testid="cc-adj-credit">Ajustes a favor sin imputar: <b className="text-emerald-700">{fmtMoney(cc.available_cc_adjustment_credit)}</b></span>}
                    </div>
                    {canEdit && <Button size="sm" variant="outline" onClick={openAdvance} data-testid="apply-advance-btn"><HandCoins className="w-4 h-4 mr-1" />Aplicar a comprobante</Button>}
                  </div>
                )}
                {cc?.aging && cc.balance > 0 && (
                  <Card data-testid="cc-aging">
                    <CardContent className="pt-4">
                      <div className="text-xs text-muted-foreground mb-2">Antigüedad de saldos</div>
                      <div className="grid grid-cols-5 gap-2 text-center">
                        {[['por_vencer', 'Por vencer'], ['d0_30', '0-30 d'], ['d31_60', '31-60 d'], ['d61_90', '61-90 d'], ['d90p', '90+ d']].map(([k, lbl]) => (
                          <div key={k} className={`rounded-md border p-2 ${k === 'd90p' && cc.aging.buckets[k] > 0 ? 'bg-red-50 border-red-300' : ''}`}>
                            <div className="text-[10px] text-muted-foreground">{lbl}</div>
                            <div className="text-sm font-semibold">{fmtMoney(cc.aging.buckets[k])}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                <Table>
                  <TableHeader><TableRow><TableHead colSpan={6}>
                    <div className="flex flex-wrap items-end gap-3 py-1">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Desde</Label>
                        <Input type="date" value={ccFrom} onChange={(e) => setCcFrom(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="cc-filter-from" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Hasta</Label>
                        <Input type="date" value={ccTo} onChange={(e) => setCcTo(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="cc-filter-to" />
                      </div>
                      {(ccFrom || ccTo) && (
                        <Button size="sm" variant="ghost" onClick={() => { setCcFrom(''); setCcTo(''); }} className="h-8 text-xs" data-testid="cc-filter-clear">Limpiar</Button>
                      )}
                      <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer select-none" data-testid="cc-show-all-wrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={showAllCc}
                          onChange={(e) => setShowAllCc(e.target.checked)}
                          data-testid="cc-show-all"
                        />
                        Ver cuenta corriente completa
                      </label>
                      <div className="ml-auto text-[11px] text-muted-foreground self-center">
                        {ccMovementsView.length} movimiento(s) · más reciente arriba
                        {!showAllCc && <span className="ml-1 text-emerald-700">· sólo sin imputar</span>}
                      </div>
                    </div>
                  </TableHead></TableRow><TableRow><TableHead>Fecha</TableHead><TableHead>Comprobante</TableHead><TableHead>Detalle</TableHead><TableHead className="text-right">Debe</TableHead><TableHead className="text-right">Haber</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {!cc || ccMovementsView.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Sin movimientos</TableCell></TableRow> :
                      ccMovementsView.map((m, i) => {
                        const pdfUrl = m.ref_id && (m.kind === 'invoice' || m.kind === 'nc')
                          ? `${BACKEND_URL}/api/invoices/${m.ref_id}/pdf`
                          : m.ref_id && m.kind === 'payment'
                          ? `${BACKEND_URL}/api/treasury/collections/${m.ref_id}/pdf`
                          : null;
                        return (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{(m.date || '').slice(0, 10).split('-').reverse().join('/')}</TableCell>
                            <TableCell className="font-mono text-xs">{m.ref_number || '—'}</TableCell>
                            <TableCell className="text-xs">
                              {m.description}
                              {m.advance_remaining > 0 && (
                                <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800" data-testid={`cc-advance-remaining-${i}`}>
                                  <span>💰</span>
                                  <span>Remanente anticipo: {fmtMoney(m.advance_remaining)} · pendiente de imputar</span>
                                </div>
                              )}
                              {m.adjustment_remaining > 0 && (
                                <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800" data-testid={`cc-adjustment-remaining-${i}`}>
                                  <span>Remanente ajuste: {fmtMoney(m.adjustment_remaining)} · pendiente de imputar</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">{m.debit ? fmtMoney(m.debit) : ''}</TableCell>
                            <TableCell className="text-right text-xs">{m.credit ? fmtMoney(m.credit) : ''}</TableCell>
                            <TableCell className="text-center">
                              {pdfUrl ? (
                                <button
                                  type="button"
                                  onClick={() => window.open(pdfUrl, '_blank')}
                                  className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-muted text-blue-600 hover:text-blue-800 transition-colors"
                                  title={m.kind === 'invoice' ? 'Ver factura (PDF)' : m.kind === 'nc' ? 'Ver nota de crédito (PDF)' : 'Ver recibo (PDF)'}
                                  data-testid={`cc-pdf-${m.kind}-${i}`}
                                >
                                  <FileText className="w-4 h-4" />
                                </button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                {showAllCc && d.resident_id && (
                  <div className="mt-4 pt-4 border-t">
                    <AdvanceApplicationsSection
                      entityType="resident"
                      entityId={d.resident_id}
                      refreshKey={`${cc?.balance}|${cc?.available_advance}|${cc?.available_cc_adjustment_credit}`}
                      onReversed={() => loadBilling(d.resident_id)}
                    />
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
          <DialogFooter className="gap-2 flex-wrap">
            {d.resident_id && canEdit && (
              d.is_archived ? (
                <Button
                  variant="outline"
                  onClick={requestUnarchive}
                  className="mr-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="unarchive-btn"
                >
                  <ArchiveRestore className="w-4 h-4 mr-1" />Desarchivar ficha
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={requestArchive}
                  disabled={!canArchiveNow}
                  title={!canArchiveNow ? 'Para archivar, el estado debe ser "Egreso total" o "Fallecido" y debe estar cargada la fecha de egreso.' : ''}
                  className="mr-auto"
                  data-testid="archive-btn"
                >
                  <Archive className="w-4 h-4 mr-1" />Archivar ficha
                </Button>
              )
            )}
            <Button variant="outline" onClick={() => setDialog({ open: false, data: empty })}>Cancelar</Button>
            <Button onClick={save} disabled={saving || d.is_archived} data-testid="save-resident-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={collect.open} onOpenChange={(o) => !o && setCollect(emptyCollect())}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="collect-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-emerald-600" />Registrar cobranza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {cc && (
              <div className="rounded-md border p-2 text-sm bg-muted/40">
                Saldo actual: <b className={cc.balance > 0 ? 'text-red-600' : 'text-emerald-600'}>{fmtMoney(cc.balance)}</b>
                {cc.balance > 0 ? ' (deuda)' : cc.balance < 0 ? ' (a favor)' : ''}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={collect.date} onChange={(e) => setCollect({ ...collect, date: e.target.value })} /></div>
              <div className="flex items-end"><div className="w-full text-right"><div className="text-xs text-muted-foreground">Total a cobrar</div><div className="text-lg font-bold" data-testid="collect-total">{fmtMoney(collectTotal)}</div></div></div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Valores</Label>
              <Button size="sm" variant="outline" onClick={addCollectLine} data-testid="add-payment-line"><Plus className="w-3 h-3 mr-1" />Agregar valor</Button>
            </div>
            <div className="space-y-2">
              {collect.lines.map((l, i) => {
                const cb = cashBanks.find(x => x.cash_bank_id === l.cash_bank_id);
                const cats = cb?.allowed_payment_categories ?? null;
                const availPT = (paymentTypes || []).filter(pt => {
                  if (cats === null) return true;
                  if (cats.length === 0) return false;
                  return cats.includes((pt.category || '').toLowerCase());
                });
                const typeDisabled = !l.cash_bank_id;
                return (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`payment-line-${i}`}>
                  <div className="col-span-3"><Label className="text-[10px]">Caja/Banco</Label>
                    <select value={l.cash_bank_id || ''} onChange={(e) => {
                      const newCb = cashBanks.find(x => x.cash_bank_id === e.target.value);
                      const newCats = newCb?.allowed_payment_categories ?? null;
                      // Si el payment_type actual ya no es compatible, resetear
                      const currentPt = (paymentTypes || []).find(pt => pt.payment_type_id === l.payment_type_id);
                      const stillValid = !currentPt || newCats === null
                        || (newCats.length > 0 && newCats.includes((currentPt.category || '').toLowerCase()));
                      setCollect(c => ({
                        ...c,
                        lines: c.lines.map((ln, idx) => idx === i
                          ? { ...ln, cash_bank_id: e.target.value, payment_type_id: stillValid ? ln.payment_type_id : '' }
                          : ln),
                      }));
                    }} className="flex h-9 w-full rounded-md border px-1 text-xs" data-testid={`collect-cashbank-${i}`}>
                      <option value="">...</option>{cashBanks.map(cbo => <option key={cbo.cash_bank_id} value={cbo.cash_bank_id}>{cbo.name}</option>)}
                    </select></div>
                  <div className="col-span-3"><Label className="text-[10px]">Tipo</Label>
                    <select value={l.payment_type_id} onChange={(e) => setCollectLine(i, 'payment_type_id', e.target.value)} disabled={typeDisabled} title={typeDisabled ? 'Elegí primero la Caja/Banco' : ''} className="flex h-9 w-full rounded-md border px-1 text-xs disabled:bg-slate-50 disabled:cursor-not-allowed" data-testid={`collect-type-${i}`}>
                      <option value="">{typeDisabled ? 'Elegí Caja/Banco primero' : '...'}</option>
                      {availPT.map(pt => <option key={pt.payment_type_id} value={pt.payment_type_id}>{pt.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><Label className="text-[10px]">Monto</Label><MoneyInput className="h-9" value={l.amount} onChange={(e) => setCollectLine(i, 'amount', e.target.value)} data-testid={`collect-amount-${i}`} /></div>
                  <div className="col-span-3"><Label className="text-[10px]">Referencia</Label><Input className="h-9" value={l.reference} onChange={(e) => setCollectLine(i, 'reference', e.target.value)} data-testid={`collect-reference-${i}`} /></div>
                  <div className="col-span-1">{collect.lines.length > 1 && <Button variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => delCollectLine(i)} data-testid={`collect-delete-line-${i}`}><Trash2 className="w-4 h-4" /></Button>}</div>
                  {acceptsValueDate(l.payment_type_id) && (
                    <div className="col-span-12" data-testid={`collect-value-date-wrap-${i}`}>
                      <Label className="text-[10px] text-sky-700 font-semibold">Fecha valor (acreditación en banco)</Label>
                      <Input type="date" className="h-9 max-w-[200px] border-sky-300" value={l.value_date || ''}
                        onChange={(e) => setCollectLine(i, 'value_date', e.target.value)} data-testid={`collect-value-date-${i}`} />
                      <p className="text-[10px] text-muted-foreground mt-1">Si queda vacía se usa la fecha del recibo. El asiento contable siempre respeta la fecha del recibo.</p>
                      {l.value_date && collect.date && l.value_date < collect.date && (
                        <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`collect-value-date-warning-${i}`}>
                          <AlertTriangle className="w-3 h-3" /> La fecha valor es anterior al recibo.
                        </p>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {collect.invoices.some(i => i.selected)
                ? 'Se aplicará a los comprobantes que tildaste debajo.'
                : 'Se aplicará automáticamente a las facturas más antiguas.'}
              {' '}El excedente queda como anticipo (saldo a favor). Impacta en Tesorería.
            </p>

            {collect.invoices.length > 0 && (
              <div className="rounded-md border p-2 space-y-1" data-testid="collect-invoices-picker">
                <div className="flex justify-between items-center text-xs font-medium">
                  <span>Comprobantes pendientes</span>
                  <span>Aplicado: <b>{fmtMoney(collectAllocated)}</b> / Total: <b>{fmtMoney(collectTotal)}</b></span>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {collect.invoices.map(inv => {
                    const applyN = Number(inv.apply_amount) || 0;
                    const isPartial = inv.selected && applyN > 0 && applyN < inv.outstanding - 0.005;
                    return (
                    <div key={inv.invoice_id} className={`grid grid-cols-12 gap-1 items-center text-xs px-1 py-1 rounded ${inv.selected ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''}`}
                      data-testid={`collect-inv-${inv.invoice_number}`}>
                      <input type="checkbox" className="col-span-1 h-4 w-4" checked={inv.selected}
                        onChange={() => toggleCollectInvoice(inv.invoice_id)} />
                      <div className="col-span-5">
                        <div className="font-mono">{inv.invoice_number}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {inv.issue_date?.slice(0, 10)}
                          {inv.arca_promotion_eligible && (
                            <span className="ml-1 text-sky-700 font-semibold" title={`Origen ARCA: ${inv.arca_source_number}`}>· ⚡ ARCA-elegible</span>
                          )}
                          {isPartial && inv.arca_promotion_eligible && (
                            <span className="ml-1 text-amber-700 font-semibold" data-testid={`partial-badge-${inv.invoice_number}`}>· 🔸 Parcial</span>
                          )}
                        </div>
                      </div>
                      <div className="col-span-3 text-right">Pend. {fmtMoney(inv.outstanding)}</div>
                      <div className="col-span-3">
                        <MoneyInput className="h-7 text-xs" value={inv.apply_amount} disabled={!inv.selected}
                          onChange={(e) => setCollectInvoiceAmount(inv.invoice_id, e.target.value)} />
                      </div>
                    </div>
                  );})}
                </div>
                {collectArcaEligible && (allBankValues ? (
                  <div className="mt-2 p-2 rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" data-testid="passthrough-auto-banner">
                    <div className="text-[11px]">
                      <div className="font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-1">
                        <Zap className="w-3.5 h-3.5" /> Se emitirá comprobante ARCA automáticamente
                      </div>
                      <div className="text-emerald-800/80 dark:text-emerald-300/80">
                        Valores 100% bancarios: las ND Proforma marcadas con <b>⚡ ARCA-elegible</b> se pasan a ARCA
                        (NC de reversión + ND ARCA fecha de hoy con CbteAsoc a la factura original) y el recibo se aplica sobre el ARCA definitivo.
                      </div>
                      <div className="mt-1 text-amber-800/90 dark:text-amber-300/80">
                        <b>Cobro parcial:</b> si el importe imputado es menor al saldo pendiente,
                        se emite una <b>NC Proforma parcial</b> + <b>ND ARCA parcial</b>. La deuda remanente sigue vigente en la Proforma original.
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="flex items-start gap-2 mt-2 p-2 rounded border border-sky-300 bg-sky-50 dark:bg-sky-950/30 cursor-pointer" data-testid="passthrough-toggle-wrap">
                    <input type="checkbox" className="mt-0.5 h-4 w-4" checked={collect.passthrough_to_arca}
                      onChange={(e) => setCollect(c => ({ ...c, passthrough_to_arca: e.target.checked }))}
                      data-testid="passthrough-toggle" />
                    <div className="text-[11px]">
                      <div className="font-semibold text-sky-900 dark:text-sky-200">
                        Emitir comprobante ARCA al cobrar
                        {isMixedValues && <span className="ml-1 text-amber-700 font-normal">(pago mixto — pre-tildado)</span>}
                        {allCashValues && <span className="ml-1 text-slate-600 font-normal">(pago en efectivo — decidí vos)</span>}
                      </div>
                      <div className="text-sky-800/80 dark:text-sky-300/80">
                        Si tildás, las ND Proforma marcadas con <b>⚡ ARCA-elegible</b> se pasan a ARCA
                        (NC de reversión + ND ARCA fecha de hoy con CbteAsoc a la factura original) y el recibo se aplica sobre el ARCA definitivo.
                      </div>
                      <div className="mt-1 text-amber-800/90 dark:text-amber-300/80">
                        <b>Cobro parcial:</b> si el importe imputado es menor al saldo pendiente,
                        se emite una <b>NC Proforma parcial</b> (para bajar la deuda en la CC del residente)
                        y una <b>ND ARCA parcial</b> por lo efectivamente cobrado. La deuda remanente sigue
                        vigente en la Proforma original.
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollect(emptyCollect())}>Cancelar</Button>
            <Button onClick={submitCollect} disabled={collect.saving} data-testid="collect-submit">{collect.saving ? 'Registrando...' : 'Cobrar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adv.open} onOpenChange={(o) => !o && setAdv(emptyAdv())}>
        <DialogContent className="max-w-md" data-testid="advance-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-emerald-600" />Aplicar saldo a favor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Saldo a favor a usar *</Label>
              <select value={adv.source_key} onChange={(e) => onAdvChange('source_key', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="advance-source">
                <option value="">Seleccionar...</option>
                {advOpts.sources.map(s => <option key={srcKey(s)} value={srcKey(s)}>{s.label} — disponible {fmtMoney(s.available || 0)}</option>)}
              </select></div>
            <div><Label className="text-xs">Comprobante a cancelar *</Label>
              <select value={adv.target_key} onChange={(e) => onAdvChange('target_key', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="advance-invoice">
                <option value="">Seleccionar...</option>
                {advOpts.targets.map(t => <option key={tgtKey(t)} value={tgtKey(t)}>{t.label} — pendiente {fmtMoney(t.outstanding || 0)}</option>)}
              </select></div>
            <div><Label className="text-xs">Monto a aplicar *</Label><MoneyInput value={adv.amount} onChange={(e) => setAdv({ ...adv, amount: e.target.value })} data-testid="advance-amount" /></div>
            <p className="text-[11px] text-muted-foreground">No mueve dinero ni cambia el saldo de la ficha: solo imputa el saldo a favor al comprobante elegido. Se puede revertir desde "Aplicaciones de anticipo".</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdv(emptyAdv())}>Cancelar</Button>
            <Button onClick={submitAdvance} disabled={adv.saving} data-testid="advance-submit">{adv.saving ? 'Aplicando...' : 'Aplicar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={archiveConfirm.open}
        onOpenChange={(o) => !o && setArchiveConfirm(s => ({ ...s, open: false }))}
        title={archiveConfirm.mode === 'archive' ? 'Archivar ficha de residente' : 'Desarchivar ficha de residente'}
        description={
          archiveConfirm.mode === 'archive' ? (
            <span>
              Vas a <strong>archivar</strong> la ficha de <strong>{archiveConfirm.resident?.full_name}</strong>.
              <br /><br />
              A partir de ahora la ficha no aparecerá en el listado activo, quedará <strong>excluida de la facturación masiva mensual</strong> y no podrás registrar nuevas facturas o cobranzas. El historial completo (facturas, NC, cobranzas, cuenta corriente) se conserva.
              <br /><br />
              Si más adelante el residente reingresa, podés desarchivar la ficha desde &ldquo;Ver archivados&rdquo;.
            </span>
          ) : (
            <span>
              Vas a <strong>desarchivar</strong> la ficha de <strong>{archiveConfirm.resident?.full_name}</strong>.
              <br /><br />
              Volverá al listado activo y podrá facturarse / cobrarse normalmente. Acordate de actualizar también el estado a &ldquo;Activo&rdquo; si el residente reingresó.
            </span>
          )
        }
        confirmText={archiveConfirm.mode === 'archive' ? 'Archivar' : 'Desarchivar'}
        destructive={archiveConfirm.mode === 'archive'}
        loading={archiveConfirm.loading}
        onConfirm={confirmArchiveAction}
        testId={`archive-confirm-${archiveConfirm.mode}`}
      />

      <ImportResidentsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={fetchAll}
      />
    </div>
  );
}

export default Residents;
